<template>
	<!-- eslint-disable max-lines-->
	<ValidationForm
		#default="{ handleSubmit }"
		class="cassie-vertical-md"
		tag="div"
	>
		<SiteScannerLayout>
			<template #header-title>
				<span>
					Scan your Website
				</span>
			</template>
			<template #content>
				<StepperAccordion
					:steps="displayedSteps"
					:initial-step="currentStep + 1"
					:current-step-index="currentStep"
					:show-actions="false"
					@step-change="onStepChange"
				>
					<!-- Step 0: Scan Type -->
					<template #step-0>
						<div v-if="currentStep === 0">
							<scan-type-step
								:site-scan="siteScan"
								:scan-type-items="scanTypeItems"
								:site-scanner-full-access="siteScannerFullAccess"
								:single-url-scan-type="singleUrlScanType"
								:sitemap-file-scan-type="sitemapFileScanType"
								:sitemap-url-scan-type="sitemapUrlScanType"
								:indexed-sitemap-url-scan-type="indexedSitemapUrlScanType"
								:select-file="selectFile"
								:update-scan-request="updateScanRequest"
								:max-urls="maxUrls"
								@update:site-scan="siteScan = $event"
								@update:scanType="siteScan.scanType = $event"
								@update:domain="siteScan.domain = $event"
								@update:sitemapUrl="siteScan.sitemapUrl = $event"
							/>
						</div>
					</template>
					<!-- Step 1: Schedule -->
					<template #step-1>
						<div v-if="currentStep === 1">
							<scan-schedule-step
								:site-scan="siteScan"
								:scan-schedule-items="scanScheduleItems"
								:scan-frequency-items="scanFrequencyItems"
								:day-items="dayItems"
								:day-of-month-items="dayOfMonthItems"
								:schedule-single-scan-value="scheduleSingleScanValue"
								:schedule-recurring-scan-value="scheduleRecurringScanValue"
								:scan-date-formatted="scanDateFormatted"
								:single-url-scan-type="singleUrlScanType"
								:first-scan-date-display="dateStringWithTimezone(firstScanDate)"
								:current-server-time-formatted="currentServerTimeFormatted"
								:server-timezone="serverTimeInfo.serverTimezone"
								:today="today"
								:site-scanner-full-access="siteScannerFullAccess"
								:time24regex="time24regex"
								:update-scan-request="updateScanRequest"
							/>
						</div>
					</template>
					<!-- Step 2: Advanced Options -->
					<template #step-2>
						<div v-if="currentStep === 2">
							<scan-advanced-step
								:site-scan="siteScan"
								:login-template-items="loginTemplateItems"
								:login-template-description="loginTemplateDescription"
								:site-scanner-full-access="siteScannerFullAccess"
								:show-robots-txt-toggle="showRobotsTxtToggle"
								:update-scan-request="updateScanRequest"
								:user-agents="userAgents"
							/>
						</div>
					</template>
				</StepperAccordion>
				<!-- Confirmation Modal -->
				<scan-request-confirm-modal
					v-if="showConfirmModal"
					:value="showConfirmModal"
					:site-scan="siteScan"
					:update-scan-request="updateScanRequest"
					:brand-options="brandOptions"
					:site-scanner-full-access="siteScannerFullAccess"
					:client-multi-branded="clientMultiBranded"
					:single-brand="singleBrand"
					@cancel="showConfirmModal = false"
					@confirm="onConfirmRequest"
				/>
			</template>
			<template #footer>
				<PageActionRow class="mt-4">
					<template #actions>
						<v-row
							dense
							class="d-flex flex-grow-1 justify-center"
						>
							<v-col
								cols="12"
								md="12"
								lg="12"
								xl="8"
								class="flex-grow-1"
							>
								<div
									class="d-flex justify-space-between align-center flex-grow-1"
									style="width: 100%"
								>
									<SecondaryActionButton @click="backToOverview">
										Back to Overview
									</SecondaryActionButton>
									<span class="stepper-footer__step-indicator">
										Step {{ currentStep + 1 }} of {{ stepperSteps.length }}
									</span>

									<div class="d-flex cassie-horizontal-sm">
										<SecondaryActionButton
											:disabled="currentStep === 0"
											@click="currentStep = Math.max(0, currentStep - 1)"
										>
											Previous
										</SecondaryActionButton>
										<PrimaryActionButton
											v-if="currentStep < stepperSteps.length - 1"
											@click="handleSubmit(nextStep)"
										>
											Next
										</PrimaryActionButton>
										<PrimaryActionButton
											v-else
											@click="handleSubmit(openConfirmModal)"
										>
											Request Scan
										</PrimaryActionButton>
									</div>
								</div>
							</v-col>
						</v-row>
					</template>
				</PageActionRow>
			</template>
		</SiteScannerLayout>
	</ValidationForm>
</template>
<script>
/* eslint-disable max-lines */
import { mapGetters } from 'vuex'
import { format } from 'date-fns'
import SiteScannerLayout from './site-scanner-layout.vue'
import ValidationForm from '../../../../../../shared/components/validation-form.vue'
import PrimaryActionButton from '../../../../../../shared/components/primary-action-button.vue'
import SecondaryActionButton from '../../../../../../shared/components/secondary-action-button.vue'
import PageActionRow from '../../../../../../shared/components/page-action-row.vue'
import { requestScheduledScan } from '../../../../../../shared/utils/api/site-scanner.js'
import { clientMultiBranded, brandOptions, singleBrand, defaultBrandInSelect, waitForLoad } from '../../../../../../shared/state/brands.js'
import { showSnackbar } from '../../../../../../shared/state/snackbar.js'
import { SITE_SCANNER_FULL_ACCESS } from '../../../../../../shared/permissions/admin-portal-permissions.js'
import { DATE_FORMAT_DATE_PICKER, DATE_TIME_FORMAT_NO_SECONDS } from '../../../../../../shared/utils/date-formatting.js'
import { getServerTimezoneInfo } from '../../../../../../shared/utils/api/server-timezone.js'
import { StepperAccordion } from '../../../../components/component-library-wrapper.js'
import ScanTypeStep from './scan-type-step.vue'
import ScanScheduleStep from './scan-schedule-step.vue'
import ScanAdvancedStep from './scan-advanced-step.vue'
import ScanRequestConfirmModal from './scan-request-confirm-modal.vue'
const today = format(new Date(), DATE_FORMAT_DATE_PICKER)

export default {
	components: {
		SiteScannerLayout,
		StepperAccordion,
		ValidationForm,
		PrimaryActionButton,
		PageActionRow,
		SecondaryActionButton,
		ScanTypeStep,
		ScanScheduleStep,
		ScanAdvancedStep,
		ScanRequestConfirmModal
	},
	props: {
		scanTypes: {
			type: Array,
			default: () => []
		},
		actionTypes: {
			type: Array,
			default: () => []
		},
		loginTemplates: {
			type: Array,
			default: () => []
		},
		scanDetails: {
			type: Object,
			default: () => {}
		},
		maxUrls: Number,
		userAgents: {
			type: Array,
			default: () => []
		}
	},
	setup () {
		return { showSnackbar, defaultBrandInSelect, clientMultiBranded, singleBrand, brandOptions }
	},
	data () {
		return {
			today,
			time24regex: /^([01]\d|2[0-3]):([0-5]\d)$/,
			serverTimeInfo: {
				currentServerTime: null,
				serverTimezone: null,
				utcOffsent: null
			},
			siteScan: {
				scanName: '',
				brandId: defaultBrandInSelect.value,
				emailAddress: '',
				scanType: null,
				scanSchedule: null,
				pausePeriodPerUrl: 0,
				scanStartDate: new Date(),
				scanExcludeBeforeDate: new Date(),
				scanRunTime: '00:01',
				scanFrequency: null,
				scanDayOfWeek: null,
				scanDayOfMonth: null,
				domain: null,
				sitemapFile: null,
				sitemapUrl: null,
				skipDays: '45',
				deepCrawl: false,
				buttonSelector: null,
				allowedDomains: [],
				followRobotsTxt: false,
				pageTimeout: 5,
				headless: false,
				loginTemplate: null,
				gpcEnabled: false,
				userAgent: 0
			},
			advancedFeatureActions: [],
			showRobotsTxtToggle: false,
			currentStep: 0,
			showConfirmModal: false,
			stepperSteps: [
				{ title: 'Scan Type', subtitle: 'Choose whether to scan a URL, upload a sitemap, or provide a sitemap link.' },
				{ title: 'When to Run', subtitle: 'Schedule the scan timing' },
				{ title: 'Advanced Features', subtitle: 'Adjust advanced settings for more control.' }
			]
		}
	},
	computed: {
		...mapGetters('auth', ['productAreaPermission']),
		displayedSteps () {
			// Mirror pattern from SSO configuration: a step is complete if its index < currentStep,
			// and disabled if its index > currentStep (cannot jump forward)
			return this.stepperSteps.map((s, i) => ({
				...s,
				stepComplete: i < this.currentStep,
				disabled: i > this.currentStep
			}))
		},
		scanTypeItems () {
			return this.scanTypes.map(scanType => ({
				text: scanType.scanTypeDescription,
				value: scanType.id
			}))
		},
		scanScheduleItems () {
			return [
				{ text: 'Scan Now', value: 1 },
				{ text: 'Schedule Single Scan', value: 2 },
				{ text: 'Schedule Recurring Scan', value: 3 }
			]
		},
		scanFrequencyItems () {
			return [
				{ text: 'Weekly', value: 2 },
				{ text: 'Monthly', value: 3 },
				{ text: 'Quarterly', value: 4 }
			]
		},
		dayItems () {
			return [
				{ text: 'Monday', value: 1 },
				{ text: 'Tuesday', value: 2 },
				{ text: 'Wednesday', value: 3 },
				{ text: 'Thursday', value: 4 },
				{ text: 'Friday', value: 5 },
				{ text: 'Saturday', value: 6 },
				{ text: 'Sunday', value: 7 }
			]
		},
		dayOfMonthItems () {
			return Array.from({ length: 28 }, (_, i) => i + 1).map(day => ({
				text: day,
				value: day
			}))
		},
		scanNowValue () {
			return 1
		},
		scheduleSingleScanValue () {
			return 2
		},
		scheduleRecurringScanValue () {
			return 3
		},
		isScheduleScan () {
			return this.siteScan.scanSchedule === this.scheduleSingleScanValue || this.siteScan.scanSchedule === this.scheduleRecurringScanValue
		},
		singleUrlScanType () {
			return 0
		},
		sitemapFileScanType () {
			return 1
		},
		sitemapUrlScanType () {
			return 2
		},
		indexedSitemapUrlScanType () {
			return 3 // TBC
		},
		weeklyScanFrequency () {
			return this.siteScan.scanFrequency === 2
		},
		monthlyScanFrequency () {
			return this.siteScan.scanFrequency === 3
		},
		quarterlyScanFrequency () {
			return this.siteScan.scanFrequency === 4
		},
		siteScannerFullAccess () {
			return this.productAreaPermission(SITE_SCANNER_FULL_ACCESS)
		},
		scanFromDate () {
			return format(this.siteScan.scanStartDate, DATE_FORMAT_DATE_PICKER)
		},
		scanExcludeBeforeDate () {
			return format(this.siteScan.scanExcludeBeforeDate, DATE_FORMAT_DATE_PICKER)
		},
		firstScanDate () {
			const regex = new RegExp(this.time24regex)
			const scanRunTimeValid = regex.test(this.siteScan.scanRunTime)
			if (!scanRunTimeValid) return ''
			const serverDate = new Date(new Date(this.serverTimeInfo.currentServerTime).toDateString())
			const startRunDate = new Date(new Date(this.siteScan.scanStartDate).toDateString())
			const dateToCompare = startRunDate > serverDate ? startRunDate : serverDate
			const scanRunTimeHour = parseInt(this.siteScan.scanRunTime.substring(0, 2))
			const scanRunTimeMins = parseInt(this.siteScan.scanRunTime.substring(3, 5))
			if (this.weeklyScanFrequency) {
				let dayDiff = (this.siteScan.scanDayOfWeek - dateToCompare.getDay() < 0)
					? this.siteScan.scanDayOfWeek - dateToCompare.getDay() + 7
					: this.siteScan.scanDayOfWeek - dateToCompare.getDay()
				// Add a week if you picked 'today' but a time in the past
				if (this.currentServerTime > new Date(serverDate.setHours(scanRunTimeHour, scanRunTimeMins)).getTime() && startRunDate < serverDate && dayDiff === 0) {
					dayDiff = dayDiff + 7
				}
				const weekDate = new Date(dateToCompare.setDate(dateToCompare.getDate() + dayDiff))
				const weekDateTime = new Date(weekDate.setHours(scanRunTimeHour, scanRunTimeMins))
				return new Date(weekDateTime.setHours(scanRunTimeHour, scanRunTimeMins))
			}
			if (this.monthlyScanFrequency) {
				const targetDateTime = new Date(new Date(dateToCompare.getFullYear(), dateToCompare.getMonth(),
					this.siteScan.scanDayOfMonth).setHours(scanRunTimeHour, scanRunTimeMins))
				if (targetDateTime.getTime() > this.currentServerTime && targetDateTime.getTime() > startRunDate.getTime()) {
					return targetDateTime
				} else {
					const dayDiff = this.siteScan.scanDayOfMonth - dateToCompare.getDate()
					return dayDiff < 0 || (dayDiff === 0 && new Date(serverDate.setHours(scanRunTimeHour, scanRunTimeMins)).getTime() < this.currentServerTime)
						? new Date(new Date(new Date(dateToCompare.setHours(scanRunTimeHour,
							scanRunTimeMins)).setMonth(dateToCompare.getMonth() + 1)).setDate(this.siteScan.scanDayOfMonth))
						: new Date(new Date(dateToCompare.setHours(scanRunTimeHour, scanRunTimeMins)).setDate(this.siteScan.scanDayOfMonth))
				}
			}
			if (this.quarterlyScanFrequency) {
				const q1 = new Date(new Date(dateToCompare.getFullYear(), 0, this.siteScan.scanDayOfMonth).setHours(scanRunTimeHour, scanRunTimeMins))
				const q2 = new Date(new Date(dateToCompare.getFullYear(), 3, this.siteScan.scanDayOfMonth).setHours(scanRunTimeHour, scanRunTimeMins))
				const q3 = new Date(new Date(dateToCompare.getFullYear(), 6, this.siteScan.scanDayOfMonth).setHours(scanRunTimeHour, scanRunTimeMins))
				const q4 = new Date(new Date(dateToCompare.getFullYear(), 9, this.siteScan.scanDayOfMonth).setHours(scanRunTimeHour, scanRunTimeMins))
				if (dateToCompare.getTime() > q1.getTime() && dateToCompare.getTime() < q2.getTime())	return q2
				if (dateToCompare.getTime() > q2.getTime() && dateToCompare.getTime() < q3.getTime())	return q3
				if (dateToCompare.getTime() > q3.getTime() && dateToCompare.getTime() < q4.getTime())	return q4
				else return new Date(q1.setFullYear(q1.getFullYear() + 1))
			} else return ''
		},
		currentServerTimeFormatted () {
			return format(new Date(new Date(this.serverTimeInfo?.currentServerTime).getTime()), DATE_TIME_FORMAT_NO_SECONDS)
		},
		scanDate () {
			const scanDatePart = new Date(this.siteScan.scanStartDate)
			const scanTime = this.siteScan.scanRunTime.split(':')
			scanDatePart.setHours(scanTime[0], scanTime[1])
			return scanDatePart
		},
		scanDateFormatted () {
			return format(new Date(this.scanDate), DATE_TIME_FORMAT_NO_SECONDS)
		},
		loginTemplateItems () {
			return this.loginTemplates.map(loginTemplate => ({
				text: loginTemplate.templateName,
				value: loginTemplate.templateGuid,
				brandId: loginTemplate.brandId
			})).filter(loginTemplate => loginTemplate.brandId === this.siteScan.brandId || loginTemplate.brandId === 0 || this.siteScan.brandId === 0)
		},
		loginTemplateDescription () {
			const loginTemplate = this.loginTemplates.find(template => template.templateGuid === this.siteScan.loginTemplate)
			return loginTemplate ? loginTemplate.templateDescription : ''
		}
	},
	async created () {
		await waitForLoad()
		const timezoneResponse = await getServerTimezoneInfo()
		const defaultAgent = this.userAgents.find(agent => agent.isDefault)
		this.siteScan.userAgent = this.scanDetails?.userAgent ?? defaultAgent.id
		await this.setDefaultBrand()
		this.serverTimeInfo = timezoneResponse.data
		if (this.scanTypes.length === 0 || this.actionTypes.length === 0 || this.maxUrls === undefined) {
			this.backToOverview()
		}
		// Populate details from existing scan if prop is passed
		if (this.scanDetails) {
			this.populateFromExistingScan()
		}
	},
	methods: {
		updateScanRequest (key, value) {
			this.siteScan[key] = value
		},
		onStepChange (oneBasedIndex) {
			// Only allow navigation to previous (completed) steps
			const target = oneBasedIndex - 1
			if (target >= 0 && target < this.currentStep) this.currentStep = target
		},
		submitStep () { this.requestSiteScan() },
		onConfirmRequest () {
			this.showConfirmModal = false
			this.requestSiteScan()
		},
		async setDefaultBrand () {
			this.siteScan.brandId = defaultBrandInSelect.value
		},
		async requestSiteScan () {
			this.requestSchedule()
		},
		customToISOString (date) {
			const year = date.getFullYear()
			const month = String(date.getMonth() + 1).padStart(2, '0')
			const day = String(date.getDate()).padStart(2, '0')
			const hours = String(date.getHours()).padStart(2, '0')
			const minutes = String(date.getMinutes()).padStart(2, '0')
			const seconds = String(date.getSeconds()).padStart(2, '0')
			return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`
		},
		async requestSchedule () {
			const requestScanForm = new FormData()
			requestScanForm.append('ScanName', this.siteScan.scanName)
			requestScanForm.append('BrandId', this.siteScan.brandId)
			requestScanForm.append('EmailAddressToNotify', this.siteScan.emailAddress)
			requestScanForm.append('ScheduledScanDateAndTime', this.siteScan.scanSchedule === this.scanNowValue ? this.customToISOString(await this.requestNowScanDate()) : this.siteScan.scanFrequency === 1 ? this.customToISOString(this.scanDate) : this.customToISOString(this.firstScanDate))
			requestScanForm.append('DeepCrawl', this.setDeepCrawl(this.siteScan.deepCrawl))
			if (this.siteScan.buttonSelector) {
				this.generateActions()
				this.advancedFeatureActions.forEach(action => {
					requestScanForm.append('Actions', JSON.stringify(action))
				})
			}
			requestScanForm.append('UseRobots', this.siteScan.followRobotsTxt)
			if (this.siteScan.allowedDomains.length >= 1) {
				requestScanForm.append('AlternativeDomains', this.siteScan.allowedDomains.join(','))
			}
			if (this.siteScan.scanType === this.sitemapFileScanType && this.siteScan.sitemapFile) requestScanForm.append('SiteMapFile', this.siteScan.sitemapFile)
			if (this.siteScan.scanType === this.sitemapUrlScanType && this.siteScan.sitemapUrl) requestScanForm.append('SiteMapUrl', this.siteScan.sitemapUrl.trim())
			if (this.siteScan.scanType === this.singleUrlScanType && this.siteScan.domain) requestScanForm.append('Domain', this.siteScan.domain.trim())
			if (this.siteScan.scanExcludeBeforeDate && this.siteScan.scanSchedule === this.scheduleRecurringScanValue) {
				requestScanForm.append('SkipDays', this.siteScan.scanExcludeBeforeDate.toISOString())
			}
			requestScanForm.append('ScanType', this.siteScan.scanType)
			requestScanForm.append('ScanDelayPeriod', this.siteScan.pausePeriodPerUrl)
			requestScanForm.append('PageTimeout', this.siteScan.pageTimeout * 1000)
			if (this.siteScan.scanSchedule === this.scheduleRecurringScanValue) {
				if (this.weeklyScanFrequency)	requestScanForm.append('Day', this.siteScan.scanDayOfWeek)
				else requestScanForm.append('Day', this.siteScan.scanDayOfMonth)
			}
			requestScanForm.append('Frequency', this.siteScan.scanFrequency)
			requestScanForm.append('GpcEnabled', this.siteScan.gpcEnabled ?? false)
			requestScanForm.append('Headless', this.siteScan.headless)
			if (this.siteScan.loginTemplate) requestScanForm.append('LoginTemplateGuid', this.siteScan.loginTemplate)
			requestScanForm.append('UserAgent', this.siteScan.userAgent)
			await requestScheduledScan(requestScanForm).then(() => {
				this.showSnackbar('Scan requested successfully')
				this.backToOverview()
			}).catch(e => {
				this.showSnackbar({ color: 'red', text: e.response.data[0] })
			})
		},
		backToOverview () {
			this.$router.push({ name: 'site-scanner' })
		},
		nextStep () {
			if (this.currentStep < this.stepperSteps.length - 1) this.currentStep++
		},
		openConfirmModal () {
			this.showConfirmModal = true
		},
		selectFile (file) {
			this.siteScan.sitemapFile = file
		},
		updateScanFromDate (date) {
			this.siteScan.scanStartDate = new Date(date)
		},
		updateScanExcludeBeforeDate (date) {
			this.siteScan.scanExcludeBeforeDate = new Date(date)
		},
		dateStringWithTimezone (dateToConvert) {
			if (dateToConvert !== '')	{
				const date = new Date(dateToConvert)
				const formattedDate = format(new Date(date.getTime()), DATE_TIME_FORMAT_NO_SECONDS)
				return formattedDate + ' ' + this.serverTimeInfo.serverTimezone
			}
			return '\'Scan Run Time\' not set ' + this.serverTimeInfo.serverTimezone
		},
		// schedule change & allowed domains handled via child components now
		setDeepCrawl (bool) {
			if (this.siteScan.scanType !== this.singleUrlScanType) return false
			else return !bool
		},
		async requestNowScanDate () {
			const timezoneResponse = await getServerTimezoneInfo()
			this.serverTimeInfo = timezoneResponse.data
			const newRequestNowDate = new Date(this.serverTimeInfo?.currentServerTime)
			newRequestNowDate.setSeconds(newRequestNowDate.getSeconds() + 30)
			return newRequestNowDate
		},
		generateActions () {
			const waitForElementActionType = this.actionTypes.find(actionType => actionType.actionTypeDescription === 'WaitForElement')
			const clickActionType = this.actionTypes.find(actionType => actionType.actionTypeDescription === 'Click')
			const waitActionType = this.actionTypes.find(actionType => actionType.actionTypeDescription === 'Wait')

			const actions = [
				{
					type: waitForElementActionType.id,
					selector: this.siteScan.buttonSelector,
					timeout: 5000
				},
				{
					type: clickActionType.id,
					selector: this.siteScan.buttonSelector,
					force: false
				},
				{
					type: waitActionType.id,
					timeout: 5000
				}
			]
			this.advancedFeatureActions = actions
		},
		populateFromExistingScan () {
			this.siteScan.scanName = this.scanDetails.scanName ?? ''
			this.siteScan.brandId = this.scanDetails.brandId ?? defaultBrandInSelect.value
			this.siteScan.emailAddress = this.scanDetails.notificationEmailAddress ?? ''
			this.populateScanType()
			this.populateScanSchedule()
			this.populateLoginTemplate()
			this.populateAlternativeDomains()
			this.populateButtonSelector()
			this.siteScan.sitemapUrl = this.scanDetails.xmlSiteMapUrl
			this.siteScan.deepCrawl = this.setDeepCrawl(this.scanDetails.deepCrawl)
			this.siteScan.followRobotsTxt = this.scanDetails.useRobots
			this.siteScan.headless = this.scanDetails.headless
			// pageTimeout in scanDetails is usually ms. If it's large (>1000) treat as ms, else assume already seconds.
			{
				const raw = this.scanDetails.pageTimeout
				if (raw === 0) {
					// Explicit 0 means "no timeout" (leave as 0)
					this.siteScan.pageTimeout = 0
				} else {
					const seconds = raw > 1000 ? (raw / 1000) : raw
					this.siteScan.pageTimeout = Math.min(10, Math.max(1, Math.round(seconds)))
				}
			}
			this.siteScan.pausePeriodPerUrl = this.scanDetails.scanDelayPeriod
			this.siteScan.gpcEnabled = this.scanDetails.gpcEnabled ?? false
		},
		populateScanType () {
			if (!this.scanDetails.xmlSiteMapOriginalFileName && !this.scanDetails.xmlSiteMapUrl) {
				this.siteScan.scanType = this.singleUrlScanType
				this.siteScan.domain = this.scanDetails.domain
			}
			if (this.scanDetails.xmlSiteMapUrl) {
				this.siteScan.scanType = this.sitemapUrlScanType
				this.siteScan.xmlSiteMapUrl = this.scanDetails.xmlSiteMapUrl
			}
		},
		populateScanSchedule () {
			// if within 5 minutes of now, treat as 'scan now'
			if (new Date(this.scanDetails.scheduledDateTime) <= new Date(new Date(this.serverTimeInfo?.currentServerTime).getTime() + 5 * 60000)) {
				this.siteScan.scanSchedule = 1
				this.siteScan.scanFrequency = 1
				return
			}
			const [dateWithoutTime, timeWithoutDate] = this.scanDetails.scheduledDateTime.split('T')
			if (new Date(this.scanDetails.scheduledDateTime) <= new Date(this.serverTimeInfo?.currentServerTime) && this.scanDetails.frequency === 1) {
				this.siteScan.scanSchedule = 1
			}
			if (new Date(this.scanDetails.scheduledDateTime) > new Date(this.serverTimeInfo.currentServerTime)) {
				this.siteScan.scanSchedule = 2
				this.siteScan.scanStartDate = new Date(dateWithoutTime)
				this.siteScan.scanRunTime = timeWithoutDate.substring(0, 5)
			}
			this.siteScan.scanFrequency = this.scanDetails.frequency
			if (this.scanDetails.frequency > 1 && this.scanDetails.day) {
				this.siteScan.scanSchedule = 3
				switch (this.scanDetails.frequency) {
					case 2:
						this.siteScan.scanDayOfWeek = this.scanDetails.day
						break
					case 3:
						this.siteScan.scanDayOfMonth = this.scanDetails.day
						break
					case 4:
						this.siteScan.scanDayOfMonth = this.scanDetails.day
						break
				}
				this.siteScan.scanStartDate = new Date(this.scanDetails.scheduledDateTime) > new Date(this.serverTimeInfo.currentServerTime)
					? new Date(dateWithoutTime)
					: new Date(this.serverTimeInfo.currentServerTime)
				if (this.scanDetails.scanExclusionPeriod && this.siteScan.scanSchedule !== 1) {
					this.siteScan.scanExcludeBeforeDate = new Date(this.scanDetails.scanExclusionPeriod)
				}
				this.siteScan.scanRunTime = timeWithoutDate.substring(0, 5)
			}
		},
		populateLoginTemplate () {
			if (this.scanDetails.loginTemplateGuid) {
				const selectedLoginTemplate = this.loginTemplates.find(template =>
					template.templateGuid === this.scanDetails.loginTemplateGuid
				)
				if (selectedLoginTemplate) {
					this.siteScan.loginTemplate = selectedLoginTemplate.templateGuid
				}
			}
		},
		populateAlternativeDomains () {
			if (this.scanDetails.alternativeDomains && this.scanDetails.alternativeDomains !== '') {
				this.siteScan.allowedDomains = this.scanDetails.alternativeDomains
					? this.scanDetails.alternativeDomains.split(',').map(domain => domain.trim())
					: []
			}
		},
		populateButtonSelector () {
			if (this.scanDetails.actions && this.scanDetails.actions.length > 0) {
				const firstValidAction = this.scanDetails.actions.find(action =>
					action && action.selector)
				this.siteScan.buttonSelector = firstValidAction.selector
			}
		}
	}
}
</script>
